import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, ModalContent, Button } from "@nextui-org/react"; 
import { ExclamationCircleIcon, XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import Select from 'react-select'; 
import { BASE_URL, getFetcher } from "../../../../services/api";
import toast from 'react-hot-toast'; 
import { bacaJawabanUnggahan } from '../../../../services/errorMessage';

const initialFormData = {
  typeSurat: '', // TIPE SURAT -> TIPE PERMOHONAN
  kodePermohonan: '',
  nomorSuratRekomendasi: '',
  tanggalSuratRekomendasi: '', // TANGGAL SURAT REKOMENDASI -> TANGGAL TERBIT
  tanggalBerakhir: '', // TANGGAL BERAKHIR -> BERLAKU SAMPAI
  kendaraan: [ // JENIS KENDARAAN -> KENDARAAN
    { noPolisi: '', merkModel: '', tahunPembuatan: '', noRangka: '', noMesin: '' }
  ],
  bahan_b3: [ // JENIS B3 -> BAHAN B3
    { id: '', cas_number: '', nama_bahan: '', karakteristik: [], fasa: '', jenis_kemasan: '', tujuan_penggunaan: 'pengangkutan' } // KARAKTERISTIK: multi-select (array)
  ],
  file_sk: null,
};

export default function ModalSKManual({ isOpen, onClose, selectedCompany, onSuccess }) {
  const [companyInfo, setCompanyInfo] = useState({ name: '-', penanggungJawab: '-', idCompanySimpel: '-', alamatKantor: '-' }); // + ALAMAT KANTOR
  const [masterBahan, setMasterBahan] = useState([]);
  const [loadingBahan, setLoadingBahan] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  // --- DRAGGABLE LOGIC ---
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  useEffect(() => { 
    if (!isOpen) { 
      setPosition({ x: 0, y: 0 });
      setFormData(initialFormData); 
    }
  }, [isOpen]);

  const handlePointerDown = (e) => {
    if (e.target.closest('.modal-header') && !e.target.closest('button')) {
        setIsDragging(true); 
        dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y }; 
        e.target.setPointerCapture(e.pointerId);
    }
  };
  const handlePointerMove = (e) => { if (isDragging) setPosition({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y }); };
  const handlePointerUp = (e) => { setIsDragging(false); if(e.target.hasPointerCapture(e.pointerId)) e.target.releasePointerCapture(e.pointerId); };

  // --- API FETCHERS ---
  useEffect(() => {
    if (isOpen && selectedCompany?.value) {
      getFetcher(`/api/company/search-company?companyIds=${selectedCompany.value}&returnAll=true`)
        .then(data => {
          const target = data?.data?.[0] || data?.[0] || {};
          setCompanyInfo({ 
            id: target?.id || selectedCompany.value,
            name: target?.name || selectedCompany.label || '-', 
            penanggungJawab: target?.penanggungJawab || '-', 
            idCompanySimpel: target?.idCompanySimpel || '-',
            alamatKantor: target?.alamatKantor || '-' // AMBIL ALAMAT KANTOR
          });
        });
    }
  }, [isOpen, selectedCompany]);

  useEffect(() => {
    if (isOpen) {
      setLoadingBahan(true);
      getFetcher('/api/data-master/bahan-b3?returnAll=true')
        .then(data => setMasterBahan(data?.data || data || []))
        .finally(() => setLoadingBahan(false));
    }
  }, [isOpen]);

  const bahanOptions = useMemo(() => {
    return masterBahan.map(b => ({
      value: b.casNumber,
      label: `${b.namaBahanKimia} / ${b.casNumber}` 
    }));
  }, [masterBahan]);

  // Opsi Karakteristik/Klasifikasi (untuk multi-select)
  const karakteristikOptions = useMemo(() => [
    'Mudah Meledak', 'Pengoksidasi', 'Sangat Mudah Sekali Terbakar', 'Sangat Mudah Terbakar',
    'Mudah Terbakar', 'Amat Sangat Beracun', 'Sangat Beracun', 'Beracun', 'Berbahaya',
    'Korosif', 'Bersifat Iritasi', 'Berbahaya Bagi Lingkungan', 'Karsinogenik',
    'Teratogenik', 'Mutagenik'
  ].map(v => ({ value: v, label: v })), []);

  // --- LOGIC KODE OTOMATIS & LAINNYA ---
  useEffect(() => {
    if (formData.tipe_permohonan) {
      let prefix = '';
      if (formData.tipe_permohonan === 'Permohonan Baru') prefix = 'A';
      else if (formData.tipe_permohonan === 'Perpanjangan') prefix = 'B';
      else if (formData.tipe_permohonan === 'Penambahan Jenis Kendaraan') prefix = 'C';
      else if (formData.tipe_permohonan === 'Penambahan Jenis B3') prefix = 'D';

      const now = new Date();
      const generatedCode = `${prefix}${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getTime()).slice(-4)}`;
      
      setFormData(prev => ({ ...prev, kodePermohonan: generatedCode }));
    } else {
      setFormData(prev => ({ ...prev, kodePermohonan: '' }));
    }
  }, [formData.tipe_permohonan]);

  const handleTanggalSuratChange = (e) => {
    const selectedDate = e.target.value;
    setFormData(prev => {
      let newBerlakuSampai = '';
      if (selectedDate) {
        const d = new Date(selectedDate);
        d.setFullYear(d.getFullYear() + 5);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        newBerlakuSampai = `${year}-${month}-${day}`;
      }
      return { ...prev, tanggalSuratRekomendasi: selectedDate, tanggalBerakhir: newBerlakuSampai };
    });
  };

  const yearsOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 22 }, (_, i) => currentYear - i);
  }, []);

  const handleAddKendaraan = () => {
    setFormData(prev => ({
      ...prev,
      kendaraan: [...prev.kendaraan, { noPolisi: '', merkModel: '', tahunPembuatan: '', noRangka: '', noMesin: '' }]
    }));
  };
  const handleRemoveKendaraan = (indexToRemove) => {
    setFormData(prev => ({ ...prev, kendaraan: prev.kendaraan.filter((_, i) => i !== indexToRemove) }));
  };
  const handleKendaraanChange = (index, field, value) => {
    setFormData(prev => {
      const newArr = [...prev.kendaraan];
      newArr[index][field] = value;
      return { ...prev, kendaraan: newArr };
    });
  };

  const handleAddB3 = () => {
    setFormData(prev => ({
      ...prev,
      bahan_b3: [...prev.bahan_b3, { id: '', cas_number: '', nama_bahan: '', karakteristik: [], fasa: '', jenis_kemasan: '', tujuan_penggunaan: null }]
    }));
  };
  const handleRemoveB3 = (indexToRemove) => {
    setFormData(prev => ({ ...prev, bahan_b3: prev.bahan_b3.filter((_, i) => i !== indexToRemove) }));
  };
  const handleB3Change = (index, field, value) => {
    setFormData(prev => {
      const newBahanB3 = [...prev.bahan_b3];
      if (field === 'cas_number') {
        const bahanObj = masterBahan.find(b => b.casNumber === value);
        newBahanB3[index] = {
           ...newBahanB3[index],
           id: bahanObj?.id,
           cas_number: value,
           nama_bahan: bahanObj?.namaBahanKimia || '',
           nama_dagang: bahanObj?.namaDagang || '' // + NAMA DAGANG DEFAULT
        };
      } else {
        newBahanB3[index][field] = value;
      }
      return { ...prev, bahan_b3: newBahanB3 };
    });
  };

  // --- SUBMIT ---
  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (!formData.tipe_permohonan) return toast.error("Silakan pilih Tipe Permohonan.");
    if (!formData.nomorSuratRekomendasi) return toast.error("Nomor SK Rekomendasi wajib diisi.");
    if (!formData.tanggalSuratRekomendasi) return toast.error("Tanggal Surat Rekomendasi wajib diisi.");
    if (!formData.tanggalBerakhir) return toast.error("Tanggal Berakhir wajib diisi."); 

    // Validasi Kendaraan Semua Wajib
    if (['Permohonan Baru', 'Penambahan Jenis Kendaraan'].includes(formData.tipe_permohonan)) {
      if (formData.kendaraan.length === 0) return toast.error("Silakan tambah minimal 1 Jenis Kendaraan.");
      for (let i = 0; i < formData.kendaraan.length; i++) {
        const k = formData.kendaraan[i];
        if (!k.noPolisi || !k.merkModel || !k.tahunPembuatan || !k.noRangka || !k.noMesin) {
          return toast.error(`Mohon lengkapi seluruh data Kendaraan pada baris ke-${i + 1}`);
        }
      }
    }

    // Validasi B3 Semua Wajib
    if (['Permohonan Baru', 'Penambahan Jenis B3'].includes(formData.tipe_permohonan)) {
      if (formData.bahan_b3.length === 0) return toast.error("Silakan tambah minimal 1 Jenis B3.");
      for (let i = 0; i < formData.bahan_b3.length; i++) {
        const b = formData.bahan_b3[i];
        if (!b.cas_number || !Array.isArray(b.karakteristik) || b.karakteristik.length === 0 || !b.fasa || !b.jenis_kemasan) {
          return toast.error(`Mohon lengkapi seluruh data B3 pada baris ke-${i + 1}`);
        }
      }
    }
    
    if (!formData.file_sk) {
      return toast.error("Mohon unggah file SK Anda.");
    }

    setShowConfirm(true);
  };

  const handleExecuteSave = async () => {
    setShowConfirm(false); 
    setSubmitting(true);
    
    const payload = new FormData();
    Object.keys(formData).forEach(k => {
      if (k === 'file_sk') {
        if (formData[k]) payload.append(k, formData[k]);
      } else if (k === 'kendaraan') {
        // Jika tipe permohonan "Penambahan Jenis B3", kirim array kosong (section kendaraan di-disable)
        const kendaraanData = formData.tipe_permohonan === 'Penambahan Jenis B3' ? [] : formData[k];
        const appendedData = kendaraanData.map(veh => ({ ...veh, kepemilikan: companyInfo.name }));
        payload.append(k, JSON.stringify(appendedData));
      } else if (k === 'bahan_b3') {
        // Jika tipe permohonan "Penambahan Jenis Kendaraan", kirim array kosong (section B3 di-disable)
        const b3Data = formData.tipe_permohonan === 'Penambahan Jenis Kendaraan' ? [] : formData[k];
        // Gabungkan array karakteristik menjadi string dipisah koma (backend menyimpan sebagai string)
        const b3Mapped = b3Data.map(b => ({
          ...b,
          karakteristik: Array.isArray(b.karakteristik) ? b.karakteristik.join(', ') : b.karakteristik
        }));
        payload.append(k, JSON.stringify(b3Mapped));
      } else if (Array.isArray(formData[k])) {
        payload.append(k, JSON.stringify(formData[k]));
      } else {
        payload.append(k, formData[k]);
      }
    });

    payload.append('companyId', companyInfo.id);
    payload.append('idCompanySimpel', companyInfo.idCompanySimpel);
    
    // NAMA & ALAMAT PEMOHON DEFAULT (Sesuai parameter backend)
    payload.append('nama_pemohon', companyInfo.penanggungJawab);
    payload.append('alamat_pemohon', companyInfo.alamatKantor);
    payload.append('tipeSurat',formData.tipe_permohonan);
    //payload.append('nomor_surat_formatted', formData.nomor_surat); // Fallback

    const token = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
    
    try {
      // UBAH ENDPOINT KE YANG BARU
      const res = await fetch(`${BASE_URL}/api/sk-manual/rekomendasi`, { 
        method: 'POST', 
        headers: { 'Authorization': `Bearer ${token}` }, 
        body: payload 
      });
      const result = await bacaJawabanUnggahan(res);
      if (res.ok) { 
        toast.success("BERHASIL! SK Rekomendasi Manual Berhasil disimpan."); 
        setFormData(initialFormData); 
        onSuccess(); 
        onClose(); 
      } else {
        toast.error(`GAGAL MENYIMPAN! Keterangan: ${result.message || 'Error server.'}`);
      }
    } catch (e) { 
      toast.error(`Terjadi kesalahan: ${e.message}`); 
    } finally { 
      setSubmitting(false); 
    }
  };

  // --- KONDISI DISABLE SECTION BERDASARKAN TIPE PERMOHONAN ---
  // Jika "Penambahan Jenis Kendaraan" -> section Jenis B3 dinonaktifkan
  // Jika "Penambahan Jenis B3" -> section Jenis Kendaraan dinonaktifkan
  const isB3Disabled = formData.tipe_permohonan === 'Penambahan Jenis Kendaraan';
  const isKendaraanDisabled = formData.tipe_permohonan === 'Penambahan Jenis B3';

  // --- STYLING (Diperkecil height dan paddingnya) ---
  const inputClass = "w-full border border-line-strong rounded px-1.5 py-1 h-7 text-[11px] bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition";
  const labelClass = "text-[10px] font-semibold text-ink-700 mb-0.5 block overflow-hidden text-ellipsis whitespace-nowrap leading-tight";
  const sectionClass = "bg-surface-2/50 border border-line rounded-lg p-2 mb-1 space-y-1.5 transition-all duration-200 focus-within:border-blue-400 focus-within:bg-blue-50/30";
  const rowHighlightClass = "flex gap-2 items-start bg-white p-1.5 rounded-md border border-line shadow-sm relative transition-all duration-200 hover:border-line-strong focus-within:bg-blue-50/60 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200";
  const infoLabelClass = "text-[9px] font-semibold text-ink-500 mb-0 block leading-tight";
  const infoDisplayClass = "w-full border border-line/80 rounded px-1.5 h-5 text-[10px] bg-surface-3/50 text-ink-700 flex items-center font-medium overflow-hidden text-ellipsis whitespace-nowrap";

  const reactSelectStyles = {
    control: (base) => ({ ...base, minHeight: '28px', height: '28px', fontSize: '11px', borderRadius: '0.25rem', borderColor: '#C7D2CF', boxShadow: 'none', '&:hover': { borderColor: '#147DEF' } }),
    menu: (base) => ({ ...base, fontSize: '11px', zIndex: 9999 }), 
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    valueContainer: (base) => ({ ...base, padding: '0 6px', height: '28px',  }), 
    input: (base) => ({ ...base, margin: 0, padding: 0 }), 
    indicatorsContainer: (base) => ({ ...base, height: '28px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '2px' }), 
    clearIndicator: (base) => ({ ...base, padding: '2px' }),
    multiValue: (base) => ({ ...base, background: '#e2e8f0', borderRadius: '4px', margin: '1px' }),
    multiValueLabel: (base) => ({ ...base, fontSize: '10px', padding: '1px 3px' }),
    multiValueRemove: (base) => ({ ...base, padding: '0 1px', '&:hover': { background: '#fecaca', color: '#dc2626' } }),
  };

  return (
    <>
      <Modal isDismissable={false} isOpen={isOpen && !showConfirm} onClose={onClose} size="full" hideCloseButton className="bg-transparent shadow-none" disableAnimation PortalGroup={false}>
        <ModalContent className="flex items-center justify-center pointer-events-none p-4">
          <div 
            style={{ transform: `translate(${position.x}px, ${position.y}px)` }} 
            className="pointer-events-auto bg-white rounded-xl shadow-2xl flex flex-col font-sans max-w-[70vw] w-full h-auto max-h-[95vh] border border-line overflow-hidden"
          >
            <form onSubmit={handlePreSubmit} className="flex flex-col h-full overflow-hidden p-0 m-0">
              
              {/* HEADER */}
              <div 
                onPointerDown={handlePointerDown} 
                onPointerMove={handlePointerMove} 
                onPointerUp={handlePointerUp} 
                className="modal-header px-4 py-2 bg-gradient-to-r from-g-800 to-g-700 flex justify-between items-center cursor-move flex-shrink-0"
              >
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5 opacity-60">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div><div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div><div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                  </div>
                  <span className="text-white font-bold text-[13px] ml-2">Form Penambahan SK Rekomendasi Manual</span>
                </div>
                <button type="button" onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded-full transition-colors"><XMarkIcon className="size-4" /></button>
              </div>

              {/* BODY (p-2.5 -> p-2, space-y-1.5) */}
              <div className="flex-1 overflow-y-auto p-2 space-y-1.5 bg-white">
                
                {/* 1. IDENTITAS PERUSAHAAN (Sangat ringkas) */}
                <div className="grid grid-cols-3 gap-2 bg-surface-2/50 p-1.5 rounded-lg border border-line items-end">
                  <div>
                    <label className={infoLabelClass}>Nama Perusahaan</label>
                    <div className={infoDisplayClass} title={companyInfo.name}>{companyInfo.name}</div>
                  </div>
                  <div>
                    <label className={infoLabelClass}>Penanggungjawab</label>
                    <div className={infoDisplayClass} title={companyInfo.penanggungJawab}>{companyInfo.penanggungJawab}</div>
                  </div>
                  <div>
                    <label className={infoLabelClass}>idCompanySimpel</label>
                    <div className={`${infoDisplayClass} font-mono text-blue-700 font-bold`} title={companyInfo.idCompanySimpel}>{companyInfo.idCompanySimpel}</div>
                  </div>
                </div>

                {/* 2. DETAIL SURAT PERMOHONAN */}
                <div className={sectionClass}>
                  <h3 className="font-bold text-[12px] border-b border-line pb-0.5 flex items-center gap-1.5 text-ink-700"><span className="w-1 h-3.5 bg-blue-600 rounded-full"></span> Detail Surat Permohonan (SK Rekomendasi)</h3>
                  <div className="flex gap-2">
                    <div className="w-[25%]">
                      <label className={labelClass}>Tipe Permohonan <span className="text-red-500">*</span></label>
                      <select required className={inputClass} value={formData.tipe_permohonan} onChange={e => setFormData(p => ({ ...p, tipe_permohonan: e.target.value }))}>
                        <option value="">-- Pilih --</option>
                        <option value="Permohonan Baru">Permohonan Baru</option>
                        <option value="Perpanjangan">Perpanjangan</option>
                        <option value="Penambahan Jenis Kendaraan">Penambahan Jenis Kendaraan</option>
                        <option value="Penambahan Jenis B3">Penambahan Jenis B3</option>
                      </select>
                    </div>
                    <div className="w-[15%]">
                      <label className={labelClass}>Kode Permohonan</label>
                      <input className={`${inputClass} bg-surface-3 text-blue-700 font-bold border-line cursor-not-allowed`} readOnly value={formData.kodePermohonan} placeholder="Otomatis" />
                    </div>
                    <div className="flex-1">
                      <label className={labelClass}>No. SK Rekomendasi <span className="text-red-500">*</span></label>
                      <input required className={inputClass} placeholder="SK.123/456..." value={formData.nomorSuratRekomendasi} onChange={e => setFormData(p => ({ ...p, nomorSuratRekomendasi: e.target.value }))} />
                    </div>
                    <div className="w-[15%]">
                      <label className={labelClass}>Tanggal SK <span className="text-red-500">*</span></label>
                      <input required type="date" className={inputClass} value={formData.tanggalSuratRekomendasi} onChange={handleTanggalSuratChange} />
                    </div>
                    <div className="w-[15%]">
                      <label className={labelClass}>Tanggal Berakhir <span className="text-red-500">*</span></label>
                      <input required type="date" className={inputClass} value={formData.tanggalBerakhir} onChange={e => setFormData(p => ({ ...p, tanggalBerakhir: e.target.value }))} />
                    </div>
                  </div>
                </div>

                {/* 3. JENIS KENDARAAN */}
                <div className={`${sectionClass} ${isKendaraanDisabled ? 'opacity-50 grayscale pointer-events-none select-none' : ''}`}>
                  <div className="border-b border-line pb-1 flex justify-between items-center">
                    <h3 className="font-bold text-[12px] flex items-center gap-1.5 text-ink-700">
                      <span className="w-1 h-3.5 bg-emerald-500 rounded-full"></span> Jenis Kendaraan
                      {isKendaraanDisabled && <span className="text-[9px] font-semibold text-red-500 ml-1">(Tidak diperlukan untuk tipe permohonan ini)</span>}
                    </h3>
                    <Button size="sm" color="success" variant="flat" isDisabled={isKendaraanDisabled} onPress={handleAddKendaraan} startContent={<PlusIcon className="size-3 stroke-2" />} className="h-5 min-w-0 text-[9px] font-bold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 border border-emerald-200 rounded px-1.5 transition-colors">
                      Tambah
                    </Button>
                  </div>
                  <fieldset disabled={isKendaraanDisabled} className="space-y-1.5 border-0 p-0 m-0">
                    {formData.kendaraan.map((k, index) => (
                      <div key={index} className={rowHighlightClass}>
                        <div className="w-[15%]"><label className={labelClass}>Nomor Polisi <span className="text-red-500">*</span></label><input required className={inputClass} placeholder="B 1234 CD" value={k.noPolisi} onChange={e => handleKendaraanChange(index, 'noPolisi', e.target.value.toUpperCase())} /></div>
                        <div className="w-[15%]"><label className={labelClass}>Merk/Model <span className="text-red-500">*</span></label><input required className={inputClass} value={k.merkModel} onChange={e => handleKendaraanChange(index, 'merkModel', e.target.value)} /></div>
                        <div className="w-[10%]">
                          <label className={labelClass}>Tahun NIK <span className="text-red-500">*</span></label>
                          <select required className={inputClass} value={k.tahunPembuatan} onChange={e => handleKendaraanChange(index, 'tahunPembuatan', e.target.value)}>
                            <option value="">--</option>
                            {yearsOptions.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                        <div className="w-[15%]"><label className={labelClass}>Nomor Rangka <span className="text-red-500">*</span></label><input required className={inputClass} value={k.noRangka} onChange={e => handleKendaraanChange(index, 'noRangka', e.target.value.toUpperCase())} /></div>
                        <div className="w-[15%]"><label className={labelClass}>Nomor Mesin <span className="text-red-500">*</span></label><input required className={inputClass} value={k.noMesin} onChange={e => handleKendaraanChange(index, 'noMesin', e.target.value.toUpperCase())} /></div>
                        <div className="flex-1">
                          <label className={labelClass}>Kepemilikan <span className="text-red-500">*</span></label>
                          <input disabled className={`${inputClass} bg-surface-3 text-ink-500 cursor-not-allowed`} value={companyInfo.name} />
                        </div>
                        {index > 0 && (
                          <button type="button" onClick={() => handleRemoveKendaraan(index)} className="absolute -right-1.5 -top-1.5 bg-white text-red-500 hover:bg-red-500 hover:text-white p-0.5 rounded-full shadow-sm transition-colors border border-red-200">
                            <XMarkIcon className="size-3 stroke-2" />
                          </button>
                        )}
                      </div>
                    ))}
                  </fieldset>
                </div>

                {/* 4. JENIS B3 */}
                <div className={`${sectionClass} ${isB3Disabled ? 'opacity-50 grayscale pointer-events-none select-none' : ''}`}>
                  <div className="border-b border-line pb-1 flex justify-between items-center">
                    <h3 className="font-bold text-[12px] flex items-center gap-1.5 text-ink-700">
                      <span className="w-1 h-3.5 bg-amber-500 rounded-full"></span> Jenis B3
                      {isB3Disabled && <span className="text-[9px] font-semibold text-red-500 ml-1">(Tidak diperlukan untuk tipe permohonan ini)</span>}
                    </h3>
                    <Button size="sm" color="warning" variant="flat" isDisabled={isB3Disabled} onPress={handleAddB3} startContent={<PlusIcon className="size-3 stroke-2" />} className="h-5 min-w-0 text-[9px] font-bold text-amber-700 bg-amber-100 hover:bg-amber-200 border border-amber-200 rounded px-1.5 transition-colors">
                      Tambah
                    </Button>
                  </div>
                  <fieldset disabled={isB3Disabled} className="space-y-1.5 border-0 p-0 m-0">
                    {formData.bahan_b3.map((b3, index) => (
                      <div key={index} className={rowHighlightClass}>
                        
                        <div className="w-[30%]">
                          <label className={labelClass}>Bahan Kimia / CAS <span className="text-red-500">*</span></label>
                          <Select
                            placeholder="Ketik/Pilih..."
                            isLoading={loadingBahan}
                            styles={reactSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            options={bahanOptions}
                            value={bahanOptions.find(o => o.value === b3.cas_number) || null}
                            onChange={(selected) => handleB3Change(index, 'cas_number', selected ? selected.value : '')}
                          />
                        </div>

                        <div className="w-[40%]">
                          <label className={labelClass}>Karakteristik/Klasifikasi <span className="text-red-500">*</span></label>
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            placeholder="-- Pilih (bisa lebih dari 1) --"
                            styles={reactSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            options={karakteristikOptions}
                            value={karakteristikOptions.filter(o => (Array.isArray(b3.karakteristik) ? b3.karakteristik : []).includes(o.value))}
                            onChange={(selected) => handleB3Change(index, 'karakteristik', selected ? selected.map(s => s.value) : [])}
                          />
                        </div>
                        
                        <div className="w-[10%]">
                          <label className={labelClass}>Fasa <span className="text-red-500">*</span></label>
                          <select required className={inputClass} value={b3.fasa} onChange={e => handleB3Change(index, 'fasa', e.target.value)}>
                            <option value="">-- Pilih --</option>
                            <option value="Padat">Padat</option>
                            <option value="Cair">Cair</option>
                            <option value="Gas">Gas</option>
                          </select>
                        </div>
                        
                        <div className="w-[20%]">
                          <label className={labelClass}>Jenis Kemasan <span className="text-red-500">*</span></label>
                          <select required className={inputClass} value={b3.jenis_kemasan} onChange={e => handleB3Change(index, 'jenis_kemasan', e.target.value)}>
                            <option value="">-- Pilih --</option>
                            <option value="Drum">Drum</option>
                            <option value="Jerigen">Jerigen</option>
                            <option value="Tangki">Tangki</option>
                            <option value="Silinder">Silinder</option>
                            <option value="Karung">Karung</option>
                            <option value="Botol">Botol</option>
                            <option value="Kotak">Kotak</option>
                            <option value="IBC">IBC</option>
                          </select>
                        </div>

                        {index > 0 && (
                          <button type="button" onClick={() => handleRemoveB3(index)} className="absolute -right-1.5 -top-1.5 bg-white text-red-500 hover:bg-red-500 hover:text-white p-0.5 rounded-full shadow-sm transition-colors border border-red-200">
                            <TrashIcon className="size-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </fieldset>
                </div>

                {/* 5. DOKUMEN LAMPIRAN SK */}
                <div className={sectionClass}>
                  <h3 className="font-bold text-[12px] border-b border-line pb-0.5 mb-1.5 flex items-center gap-1.5 text-ink-700">
                    <span className="w-1 h-3.5 bg-teal-500 rounded-full"></span> Dokumen Lampiran <span className="text-red-500">*</span>
                  </h3>
                  <div className="w-full bg-blue-50/50 p-2 rounded-lg border border-dashed border-blue-300 flex flex-col justify-center relative overflow-hidden transition-all duration-200 hover:border-blue-400 hover:bg-blue-100/50 focus-within:border-blue-500 focus-within:bg-blue-100 focus-within:ring-2 focus-within:ring-blue-500/20">
                    <label className="text-[10px] font-bold text-blue-950 mb-1 block relative z-10">
                      Unggah File SK (PDF, Max 2MB) <span className="text-red-500">*</span>
                    </label>
                    <input 
                      required
                      type="file" accept="application/pdf" 
                      className="relative z-10 text-[10px] w-full text-ink-500 file:mr-2 file:py-0.5 file:px-2 file:rounded file:border-0 file:bg-blue-600 file:text-white file:font-semibold hover:file:bg-blue-700 cursor-pointer border border-blue-200 rounded p-[2px] bg-white h-[24px] shadow-sm" 
                      onChange={e => {
                        const file = e.target.files[0];
                        if (file && file.size > 2 * 1024 * 1024) { 
                          toast.error("Ukuran file melebihi 2MB!"); 
                          e.target.value = null; 
                          return; 
                        }
                        setFormData(p => ({ ...p, file_sk: file }));
                      }}
                    />
                    {formData.file_sk && (
                        <div className="text-[9px] text-emerald-700 mt-0.5 font-medium truncate z-10">
                            Terpilih: {formData.file_sk.name}
                        </div>
                    )}
                  </div>
                </div>

              </div>

              {/* FOOTER */}
              <div className="px-4 py-2 border-t border-line bg-surface-2 flex justify-end gap-2.5 flex-shrink-0 rounded-b-xl">
                <Button size="sm" variant="bordered" onPress={onClose} className="h-7 text-[11px] font-semibold text-ink-700 border-line-strong hover:bg-surface-3 hover:border-line-strong rounded">Batal</Button>
                <Button size="sm" color="primary" type="submit" isLoading={submitting} className="h-7 text-[11px] font-semibold bg-blue-600 hover:bg-blue-700 rounded shadow-sm">Simpan Data</Button>
              </div>

            </form>
          </div>
        </ModalContent>
      </Modal>

      {/* MODAL KONFIRMASI */}
      <Modal isDismissable={false} isOpen={showConfirm} onClose={() => setShowConfirm(false)} size="sm">
        <ModalContent className="p-5 text-center">
          <ExclamationCircleIcon className="size-12 mx-auto text-amber-500 mb-3" />
          <h3 className="font-bold text-base text-ink-900 mb-2">Konfirmasi Simpan</h3>
          <p className="text-sm text-ink-500 mb-5">Pastikan data yang anda inputkan sudah benar. Apakah anda yakin ingin menambahkan Data SK tersebut?</p>
          <div className="flex gap-3">
            <Button className="flex-1 font-semibold rounded-lg" variant="flat" onPress={() => setShowConfirm(false)}>Batal</Button>
            <Button className="flex-1 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 shadow-sm" onPress={handleExecuteSave} isLoading={submitting}>Ya, Simpan</Button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
}
